import { ChangeEvent, useRef } from "react";
import { isEmpty, isEqual, isNil } from "lodash";
import { DateTime } from "luxon";

import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";

import styles from "./MandatoryFieldsFrom.module.scss";

interface IMandatoryFieldsFormValues {
    firstName: string | null;
    lastName: string | null;
    sexAtBirthLookupId: string | null;
    dateOfBirth: Date | null;
}

interface IMandatoryFieldsFormProps {
    values: IMandatoryFieldsFormValues;
    onChange: (value: IMandatoryFieldsFormValues) => void;
}

function MandatoryFieldsForm(props: IMandatoryFieldsFormProps): JSX.Element {
    const { getText } = useTranslation();

    const initialFields = useRef(props.values).current;

    const age = MyleHelper.GetFormattedAge(props.values?.dateOfBirth).replace(/[() ]/g, "");

    const isUnderAgeOfConsent = props.values?.dateOfBirth && DateTime.local().diff(DateTime.fromJSDate(props.values?.dateOfBirth), "years").years <= 13;

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
        const { name, value } = e.target;

        props.onChange({ ...props.values, [name]: value });
    };

    const handleDateChange = (date: Date | null): void => {
        props.onChange({ ...props.values, dateOfBirth: date });
    };

    return (
        <form name="infoForm" className={styles.infoForm}>
            {(isEmpty(props.values?.firstName) || !isEqual(props.values?.firstName, initialFields.firstName)) && (
                <div className={styles.formGroup}>
                    <label htmlFor="firstName">
                        <strong>{getText("PortalInvitation_AddPatientFirstNameLabel")}</strong>
                    </label>
                    <input
                        required
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={props.values?.firstName || ""}
                        onChange={handleChange}
                        className="form-control"
                    />
                </div>
            )}
            {(isEmpty(props.values?.lastName) || !isEqual(props.values?.lastName, initialFields.lastName)) && (
                <div className={styles.formGroup}>
                    <label htmlFor="lastName">
                        <strong>{getText("PortalInvitation_AddPatientLastNameLabel")}</strong>
                    </label>
                    <input
                        required
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={props.values?.lastName || ""}
                        onChange={handleChange}
                        className="form-control"
                    />
                </div>
            )}
            {(isEmpty(props.values?.sexAtBirthLookupId) || !isEqual(props.values?.sexAtBirthLookupId, initialFields.sexAtBirthLookupId)) && (
                <div className={styles.formGroup}>
                    <label htmlFor="sexAtBirthLookupId">
                        <strong>{getText("PortalInvitation_AddPatientSexAtBirthLabel")}</strong>
                    </label>
                    <select
                        required
                        id="sexAtBirthLookupId"
                        name="sexAtBirthLookupId"
                        value={props.values?.sexAtBirthLookupId || ""}
                        onChange={handleChange}
                        className="form-control"
                    >
                        <option value="" />
                        {cacheStore.GetGenderLookups()
                            .filter(option => !cacheStore.ClinicSettings_Portal.EnablePortalV2 || option.LookupCode !== "OTHERGENDER")
                            .map(option => (
                                <option key={option.LookupID} value={option.LookupID}>
                                    {cacheStore.GetLocalizedValueByLookupId(option.LookupID)}
                                </option>
                            ))}
                    </select>
                </div>
            )}
            {(isNil(props.values?.dateOfBirth) || !isEqual(props.values?.dateOfBirth, initialFields.dateOfBirth)) && (
                <div className={styles.formGroup}>
                    <label htmlFor="dateOfBirth">
                        <strong>{getText("PortalInvitation_AddPatientDateOfBirthLabel")}</strong>
                    </label>
                    <DateSelector
                        id="dateOfBirth"
                        width={150}
                        date={props.values?.dateOfBirth}
                        onChangeCallback={handleDateChange}
                        dateFormat="yyyy/MM/dd"
                        ignoreInvalidCharacters
                    />
                </div>
            )}
            {(isUnderAgeOfConsent && age && !isEqual(props.values?.dateOfBirth, initialFields.dateOfBirth)) && (
                <p className={styles.ageMessage}>
                    {getText("PortalInvitation_PatientAgeMessageLabel", { age })}
                </p>
            )}
        </form>
    );
}
export type { IMandatoryFieldsFormValues };

export { MandatoryFieldsForm };
